import * as Yup from 'yup';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, FormControlLabel, Link, Switch } from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { createEmpployee, TCreateEmployee, updateEmployee } from 'src/redux/slices/employees';

import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFTextField } from 'src/components/hook-form';

import { IEmployeeItem } from 'src/types/employee';

import { paths } from '../../routes/paths';
import { useAppDispatcher } from '../../redux/store';

export type ConditionalSchema<T> = T extends string
  ? Yup.StringSchema
  : T extends number
  ? Yup.NumberSchema
  : T extends boolean
  ? Yup.BooleanSchema
  : T extends Record<any, any>
  ? Yup.AnyObjectSchema
  : T extends Array<any>
  ? Yup.ArraySchema<any, any>
  : Yup.AnySchema;

export type Shape<Fields> = {
  [Key in keyof Fields]: ConditionalSchema<Fields[Key]>;
};

type Props = {
  currentEmployee?: IEmployeeItem;
  employeeNumber?: string;
};

export default function EmployeeNewEditForm({ currentEmployee, employeeNumber }: Props) {
  const dispatch = useAppDispatcher();

  const { enqueueSnackbar } = useSnackbar();

  const navigate = useNavigate();

  const NewEmployeeSchema = Yup.object<Shape<TCreateEmployee>>().shape({
    name: Yup.string().required('Employee name is required'),
    employeeNumber: Yup.string().required('Employee number is required'),
    baseSalary: Yup.number().required('Base salary is required'),
    epf: Yup.boolean().default(false),
  });

  const defaultValues = useMemo(
    () => ({
      name: currentEmployee?.name || '',
      employeeNumber: currentEmployee?.employeeNumber || employeeNumber || '',
      baseSalary: currentEmployee?.baseSalary || 0,
      epf: currentEmployee?.epf || false,
    }),
    [currentEmployee, employeeNumber]
  );

  const methods = useForm({
    resolver: yupResolver(NewEmployeeSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting },
    trigger,
    setValue,
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    if (currentEmployee) {
      // update
      try {
        await dispatch(
          updateEmployee({
            employeeId: currentEmployee._id,
            employee: {
              ...data,
              name: data.name.trimStart().replace(/^\w/, (c) => c.toUpperCase()),
            },
          })
        ).unwrap();

        enqueueSnackbar('Changes were made successfully');
        navigate(paths.dashboard.employees.root);
        reset();
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    } else {
      try {
        await dispatch(
          createEmpployee({
            employee: {
              ...data,
              name: data.name.trimStart().replace(/^\w/, (c) => c.toUpperCase()),
            },
          })
        ).unwrap();

        enqueueSnackbar('Employee created successfully');

        navigate(paths.dashboard.employees.root);
        reset();
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    }
  });

  // const fetchAvailableEmployeeNumber = useCallback(() => {
  //   dispatch(getNextAvaulableEmployeeNumber());
  // }, [dispatch]);

  // useEffect(() => {
  //   fetchAvailableEmployeeNumber();
  // }, [fetchAvailableEmployeeNumber]);

  useEffect(() => {
    if (employeeNumber) {
      setValue('employeeNumber', employeeNumber);
    }
  }, [employeeNumber, setValue]);

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Card sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid
                mb={1}
                display="flex"
                mt={2}
                alignItems="center"
                justifyContent="space-between"
                width={1}
              >
                <Typography variant="subtitle2">Employee Information</Typography>
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField required name="name" label="Employee Name" />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField
                  required
                  name="employeeNumber"
                  label="Employee Number"
                  disabled={!!currentEmployee}
                />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField type="number" required name="baseSalary" label="Base Salary" />
              </Grid>

              <Grid xs={12} md={6}>
                <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Controller
                      name="epf"
                      control={control}
                      render={({ field }) => <Switch {...field} checked={field.value} />}
                    />
                  }
                  label={
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Is EPF Deducting?
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Turn on the switch if the employee is eligible for EPF deduction.
                      </Typography>
                    </>
                  }
                  sx={{ mx: 0, mb: 3, width: 1, justifyContent: 'space-between' }}
                />
              </Grid>
            </Grid>

            <Stack alignItems="flex-end" sx={{ mt: 3 }}>
              <Stack direction="row" gap={2}>
                <Link component={RouterLink} href={paths.dashboard.employees.root}>
                  <Button variant="outlined" color="inherit">
                    Cancel
                  </Button>
                </Link>

                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  onClick={() => trigger()}
                >
                  {!currentEmployee ? 'Create Employee' : 'Save Changes'}
                </LoadingButton>
              </Stack>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
